import { ReactNode, useState } from "react";
import { Box, useTheme, useMediaQuery, Drawer } from "@mui/material";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/Redux/app/hooks";
import { get_login_user_details, update_profile } from "@/Redux/Actions/AuthUser";
import { useRouter } from "next/router";
import { publicPaths } from "@/lib/utils";
import TermsAndConditionsModal from "../Common/TermsAndConditionsModal";
import useSnackBar from "@/Redux/hooks/useSnackBar";
import ErrorHandler from "@/lib/errorHandler";

interface MainLayoutProps {
    children: ReactNode;
}

const MainLayout = ({ children }: MainLayoutProps) => {
    // ============= Hooks =============
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
    const dispatch = useAppDispatch();
    const router = useRouter();
    const { setSnackBar } = useSnackBar();
    const { userDetail, status } = useAppSelector((state) => state.auth.loggedInUserDetails);

    // ============= States =============
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

    // ============= Effects =============
    useEffect(() => {
        const token = sessionStorage.getItem("authToken");
        const isPublic = publicPaths.some((p) => router.asPath.startsWith(p));
        if (token && !isPublic && status === "idle") {
            dispatch(get_login_user_details());
        }
    }, [router.asPath, status, dispatch]);

    useEffect(() => {
        if (userDetail?._id && userDetail?.userType === "KITCHEN" && userDetail?.isAgreedOnPolicy === false) {
            setIsTermsModalOpen(true);
        } else {
            setIsTermsModalOpen(false);
        }
    }, [userDetail]);

    // ============= Handlers =============
    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleAgreeTerms = async () => {
        const body = {
            isAgreedOnPolicy: true
        };
        const response = await dispatch(update_profile(body));
        if (ErrorHandler(response, setSnackBar)) {
            setSnackBar("success", response?.payload?.message || "Terms and conditions accepted successfully");
            setIsTermsModalOpen(false);
            dispatch(get_login_user_details());
        }
    };

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: theme.palette.background.default }}>
            {/* Sidebar for Desktop */}
            {!isMobile && <Sidebar />}

            {/* Sidebar for Mobile */}
            {isMobile && (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: 280,
                            border: "none",
                        },
                    }}
                >
                    <Sidebar onMobileClose={() => setMobileOpen(false)} />
                </Drawer>
            )}

            <Box sx={{
                flexGrow: 1,
                ml: isMobile ? 0 : "280px",
                width: isMobile ? "100%" : "calc(100% - 280px)",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
            }}>
                <Header onMenuClick={handleDrawerToggle} isMobile={isMobile} />
                <Box component="main" sx={{ p: { xs: 2, sm: 3 }, backgroundColor: theme.palette.background.default, flexGrow: 1, overflowX: "hidden" }}>
                    {status === "success" && children}
                </Box>
            </Box>
            <TermsAndConditionsModal
                open={isTermsModalOpen}
                onClose={() => setIsTermsModalOpen(false)}
                onAgree={handleAgreeTerms}
            />
        </Box>
    );
};

export default MainLayout;
